import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Globe, Star, TrendingUp, Newspaper, ExternalLink, X } from 'lucide-react';
import { PRESS_REVIEWS, FESTIVAL_QUOTES, NEWS_ARTICLES } from '@/lib/index';
import Layout from '@/components/Layout';


const AWARD_CATEGORIES = [
  {
    id: 'lgbtq',
    title: 'Best LGBTQ Film',
    description: 'Multiple wins across international festivals',
    count: '40+'
  },
  {
    id: 'director',
    title: 'Best Director',
    description: 'Recognized across 3 continents',
    count: '35+'
  },
  {
    id: 'short',
    title: 'Best International Short',
    description: 'Celebrated at Cannes, Sundance & Berlin',
    count: '34+'
  }
];

const INDUSTRY_STATS = [
  { label: 'International Awards', value: '109+' },
  { label: 'Festival Selections', value: '50+' },
  { label: 'Countries Screened', value: '25+' },
  { label: 'Press Features', value: '100+' }
];

export default function Press() {
  const [selectedNews, setSelectedNews] = useState<string | null>(null);

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section className="relative py-16 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/50 to-background" />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-4 md:mb-6">
                PRESS & RECOGNITION
              </h1>
              <div className="h-1 w-32 bg-accent mx-auto mb-8" />
              <p className="text-xl md:text-2xl text-muted-foreground">
                Global Acclaim for Cinematic Excellence
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-secondary/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
                MAJOR PRESS
              </h2>
              <p className="text-lg text-muted-foreground">
                International Media Recognition
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8 max-w-7xl mx-auto">
              {PRESS_REVIEWS.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-card border border-border rounded-lg p-8 shadow-cinematic hover:shadow-gold transition-all duration-300"
                >
                  <div className="mb-6">
                    <h3 className="font-display text-2xl font-bold text-accent mb-2">
                      {review.publication}
                    </h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {review.location}
                    </p>
                  </div>
                  <blockquote className="text-foreground leading-relaxed italic">
                    "{review.quote}"
                  </blockquote>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
                FESTIVAL ACCLAIM
              </h2>
              <p className="text-lg text-muted-foreground">
                Celebrated at the World's Most Prestigious Film Festivals
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
              {FESTIVAL_QUOTES.map((festival, index) => (
                <motion.div
                  key={festival.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-secondary border border-border rounded-lg p-6 hover:border-accent transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Star className="w-6 h-6 text-accent" />
                    <h3 className="font-display text-lg font-semibold">
                      {festival.festival}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground italic">
                    "{festival.quote}"
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-secondary/50 to-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-3 mb-6">
                <Award className="w-12 h-12 text-accent" />
                <h2 className="font-display text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold">
                  109+ INTERNATIONAL AWARDS
                </h2>
              </div>
              <p className="text-xl text-muted-foreground">
                CATFIGHT - A Global Phenomenon
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
              {AWARD_CATEGORIES.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-card border-2 border-accent/20 rounded-lg p-8 text-center hover:border-accent transition-all duration-300"
                >
                  <div className="text-5xl font-display font-bold text-accent mb-4">
                    {category.count}
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-3">
                    {category.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {category.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
            >
              {INDUSTRY_STATS.map((stat, index) => (
                <div
                  key={index}
                  className="text-center p-6 bg-secondary/50 rounded-lg border border-border"
                >
                  <div className="text-3xl font-display font-bold text-accent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-card border-2 border-accent/30 rounded-lg p-10 shadow-gold">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-8 h-8 text-accent" />
                  <h2 className="font-display text-3xl font-bold">
                    GLOBAL CINEMA REVIEW
                  </h2>
                </div>
                <h3 className="font-display text-2xl font-semibold mb-6 text-accent">
                  The Global Cinematic Shift: Keshhav Panneriy Leading the Charge
                </h3>
                <blockquote className="text-lg leading-relaxed text-foreground italic border-l-4 border-accent pl-6">
                  "The award-winning director is not merely adapting to change — he is actively engineering the future of global cinema. With a slate of ambitious projects spanning multiple genres and continents, Panneriy represents a new generation of filmmakers who seamlessly blend artistic integrity with commercial viability, technical innovation with emotional depth."
                </blockquote>
              </div>
            </motion.div>
          </div>
        </section>

        {/* News Articles Section */}
        <section className="py-12 md:py-20 bg-secondary/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-3 mb-4">
                <Newspaper className="w-8 h-8 text-accent" />
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
                KESHHAV PANNERIY IN THE NEWS
              </h2>
              </div>
              <p className="text-lg text-muted-foreground">
                Worldwide Press Coverage — Click Any Article to Read More
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
              {NEWS_ARTICLES.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="bg-card border border-border rounded-lg overflow-hidden shadow-cinematic hover:shadow-gold hover:border-accent/50 transition-all duration-300 cursor-pointer group"
                  onClick={() => setSelectedNews(article.id)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-accent uppercase tracking-wider border border-accent/30 rounded px-2 py-1">
                        {article.category}
                      </span>
                      <span className="text-xs text-muted-foreground">{article.date}</span>
                    </div>
                    <h3 className="font-display text-base font-bold mb-3 text-foreground leading-snug group-hover:text-accent transition-colors line-clamp-3">
                      {article.headline}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-4 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-accent" />
                      <span className="text-xs text-muted-foreground">{article.publication}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">{article.location}</span>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-accent text-xs font-semibold">
                      <ExternalLink className="w-3.5 h-3.5" />
                      READ ARTICLE
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* News Popup */}
        <AnimatePresence>
          {selectedNews && (() => {
            const article = NEWS_ARTICLES.find(a => a.id === selectedNews);
            if (!article) return null;
            return (
              <motion.div
                key="news-popup"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
                onClick={() => setSelectedNews(null)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-card border-2 border-accent/30 rounded-xl p-8 max-w-lg w-full shadow-gold relative"
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setSelectedNews(null)}
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-semibold text-accent uppercase tracking-wider border border-accent/30 rounded px-2 py-1">
                      {article.category}
                    </span>
                    <span className="text-xs text-muted-foreground">{article.date}</span>
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3 text-foreground">
                    {article.headline}
                  </h3>
                  <p className="text-sm font-semibold text-accent mb-4">
                    {article.publication} · {article.location}
                  </p>
                  <p className="text-base text-muted-foreground leading-relaxed mb-6">
                    {article.summary}
                  </p>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-bold px-6 py-3 rounded hover:bg-accent/80 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    VISIT ORIGINAL ARTICLE
                  </a>
                </motion.div>
              </motion.div>
            );
          })()}
        </AnimatePresence>

        <section className="py-12 md:py-20 bg-secondary/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
                AUTEUR PARALLELS
              </h2>
              <p className="text-lg text-muted-foreground">
                In Company of Cinematic Titans
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl mx-auto"
            >
              <div className="bg-card border-2 border-accent/20 rounded-2xl p-10 md:p-14 shadow-gold">
                <div className="h-1 w-24 bg-gradient-to-r from-accent to-accent/30 rounded-full mb-10" />
                <p className="text-lg md:text-xl text-foreground leading-relaxed mb-8 text-justify">
                  In the grand tradition of filmmakers who transcend geography and genre to inscribe themselves into the permanent lexicon of world cinema, <span className="text-accent font-semibold">Keshhav Panneriy</span> occupies a singular and extraordinary position. Where <strong>Francis Ford Coppola</strong> wove the mythologies of family, power, and moral erosion across generations with unparalleled narrative architecture, Panneriy architects his own sagas — spanning centuries, continents, and the full spectrum of human consciousness — with a structural ambition that rivals the Godfather maestro himself. Where <strong>Ridley Scott</strong> forged atmospheres so immersive they became worlds unto themselves, Panneriy's lens conjures visual universes that breathe with an intensity both intimate and operatic, his command of mise-en-scène demonstrating a rare directorial sovereignty over space, light, and mood.
                </p>
                <p className="text-lg md:text-xl text-foreground leading-relaxed mb-8 text-justify">
                  Like <strong>George Lucas</strong>, who dared to dream of galaxies and mythological futures, Panneriy is building India's first true cinematic universe — a multi-volume epic spanning 1,176 years of civilizational history — while simultaneously crafting intimate human stories that resonate at the most personal frequency. Like <strong>James Cameron</strong>, who refused to accept the limitations of existing technology and simply invented the future of filmmaking, Panneriy stands at the frontier of AI-augmented cinema, CGI-VFX hybrid production, and stereoscopic 3D — not merely as an adopter of innovation, but as one of its principal architects. And like <strong>Peter Jackson</strong>, whose epic vision turned literary mountains into cinematic monuments of global proportions, Panneriy's forthcoming universes — the PC Cinematic Universe and the spiritual epic Supreme Consciousness — carry within them the DNA of a franchise-defining ambition that the global screen has never seen emerge from Indian soil.
                </p>
                <p className="text-lg md:text-xl text-foreground leading-relaxed mb-8 text-justify">
                  Yet what makes Keshhav Panneriy genuinely and irreducibly unique is that he transcends the sum of all these comparisons. He is not merely a synthesis of influences — he is a new element in the periodic table of world cinema, one who has forged his identity at the intersection of Mumbai's mass-market storytelling sensibility and Hollywood's pre-production rigor, augmented by over two decades of immersion in the performing arts and the academic discipline of Toronto Film School. His films do not merely entertain; they interrogate — society, identity, spirituality, power, love — and they do so with a visual intelligence and emotional depth that have already earned him <span className="text-accent font-semibold">110+ international awards</span> across six countries, before the full weight of his vision has even reached the world stage.
                </p>
                <p className="text-xl md:text-2xl text-accent font-semibold italic text-center leading-relaxed">
                  Keshhav Panneriy is not simply a filmmaker to watch. He is the filmmaker who will define the next chapter of global cinema — a visionary whose work will endure long after the credits of our era have rolled.
                </p>
                <div className="h-1 w-24 bg-gradient-to-r from-accent/30 to-accent rounded-full mt-10 ml-auto" />
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}